/** O histórico: as conversas do usuário, mais recente primeiro, com título,
 *  data e a primeira pergunta; busca por texto. Abrir uma continua dela.
 *  Esqueleto de linhas enquanto carrega. Duas abas desde 07/09/2026, ativas e
 *  arquivadas: arquivar deixou de ser um caminho sem volta. */
import { useMemo, useState, type MouseEvent } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import { Archive, ArchiveRestore, MoreHorizontal, Search, Trash2 } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import { HTML_LANG } from '@/core/i18n/messages'
import { SP_TZ } from '@/shared/datetime'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import {
  useApagarConversa, useArquivarConversa, useConversas, useDesarquivarConversa, type ConversaResumo,
} from './api'
import { useAssistente, useConversaDoAssistente, type AbaDoHistorico } from './AssistenteProvider'
import { EsqueletoDeLinhas } from './MensagemLista'

function dataCurta(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', timeZone: SP_TZ })
}

function tituloDe(conversa: ConversaResumo, semTitulo: string): string {
  return conversa.titulo?.trim() || conversa.primeira_pergunta?.trim() || semTitulo
}

function LinhaDaConversa({ conversa, ativa, onAbrir, onMenu }: {
  conversa: ConversaResumo
  ativa: boolean
  onAbrir: () => void
  onMenu: (el: HTMLElement) => void
}) {
  const { t, lang } = useI18n()
  const titulo = tituloDe(conversa, t('assistente.conversa.semTitulo'))
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '0 12px 0 12px',
        bgcolor: (tema) => (ativa ? alpha(tema.palette.primary.main, tema.palette.mode === 'dark' ? 0.16 : 0.07) : 'transparent'),
        '&:hover': { bgcolor: (tema) => (ativa ? alpha(tema.palette.primary.main, tema.palette.mode === 'dark' ? 0.2 : 0.1) : tema.palette.action.hover) },
        '&:hover .assistente-menu, &:focus-within .assistente-menu': { opacity: 1 },
        transition: 'background-color var(--transition-quick)',
      }}
    >
      <ButtonBase
        onClick={onAbrir}
        aria-current={ativa ? 'true' : undefined}
        aria-label={t('assistente.historico.abrir', { titulo })}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          px: 1.5,
          py: 1,
          pr: 5,
          borderRadius: 'inherit',
          '&.Mui-focusVisible': { boxShadow: 'var(--focus-ring)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 600, color: ativa ? 'primary.main' : 'text.primary' }}>
            {titulo}
          </Typography>
          <Typography sx={{ flex: 'none', fontSize: '0.6875rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
            {dataCurta(conversa.updated_at || conversa.created_at, HTML_LANG[lang])}
          </Typography>
        </Box>
        {conversa.primeira_pergunta && (
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
            {conversa.primeira_pergunta}
          </Typography>
        )}
      </ButtonBase>
      <IconButton
        size="small"
        className="assistente-menu"
        onClick={(e: MouseEvent<HTMLButtonElement>) => onMenu(e.currentTarget)}
        aria-label={t('assistente.conversa.acoes')}
        sx={{ position: 'absolute', top: 6, right: 6, color: 'text.secondary', opacity: 0, transition: 'opacity var(--transition-quick)', '&:focus-visible': { opacity: 1 } }}
      >
        <MoreHorizontal size={15} />
      </IconButton>
    </Box>
  )
}

export function ConversaLista() {
  const { t } = useI18n()
  const { ativo, abaDoHistorico, setAbaDoHistorico } = useAssistente()
  const { conversaAtual, abrirConversa, novaConversa } = useConversaDoAssistente()
  const arquivadas = abaDoHistorico === 'arquivadas'
  const { data, isLoading, isError } = useConversas(ativo, arquivadas)
  const arquivar = useArquivarConversa()
  const desarquivar = useDesarquivarConversa()
  const apagar = useApagarConversa()
  const [busca, setBusca] = useState('')
  const [menu, setMenu] = useState<{ el: HTMLElement; uuid: string } | null>(null)
  const [apagando, setApagando] = useState<string | null>(null)

  const filtradas = useMemo(() => {
    const lista = data ?? []
    const termo = busca.trim().toLowerCase()
    if (!termo) return lista
    return lista.filter((c) => `${c.titulo ?? ''} ${c.primeira_pergunta ?? ''}`.toLowerCase().includes(termo))
  }, [data, busca])

  function aoArquivar() {
    if (!menu) return
    const { uuid } = menu
    setMenu(null)
    arquivar.mutate(uuid)
    if (uuid === conversaAtual) novaConversa()
  }

  function aoDesarquivar() {
    if (!menu) return
    const { uuid } = menu
    setMenu(null)
    desarquivar.mutate(uuid)
  }

  function trocarAba(_: unknown, aba: AbaDoHistorico | null) {
    if (aba) setAbaDoHistorico(aba)
  }

  function confirmarApagar() {
    if (!apagando) return
    const uuid = apagando
    apagar.mutate(uuid, {
      onSettled: () => setApagando(null),
    })
    if (uuid === conversaAtual) novaConversa()
  }

  return (
    <Box className="anim-fade-up" sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, flex: 'none' }}>
        <TextField
          size="small"
          fullWidth
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={t('assistente.historico.buscar')}
          slotProps={{
            htmlInput: { 'aria-label': t('assistente.historico.buscar'), 'data-assistente-composer': 'true' },
            input: { startAdornment: <InputAdornment position="start"><Search size={14} /></InputAdornment> },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          fullWidth
          value={abaDoHistorico}
          onChange={trocarAba}
          aria-label={t('assistente.historico.abas')}
          sx={{ mt: 1, '& .MuiToggleButton-root': { py: 0.375, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          <ToggleButton value="ativas">{t('assistente.historico.ativas')}</ToggleButton>
          <ToggleButton value="arquivadas">{t('assistente.historico.arquivadas')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.25, pb: 2 }}>
        {isLoading && (
          <Box sx={{ px: 0.5, pt: 1, display: 'grid', gap: 2 }}>
            {[0, 1, 2, 3, 4].map((i) => <EsqueletoDeLinhas key={i} larguras={['70%', '92%']} />)}
          </Box>
        )}
        {isError && (
          <Typography sx={{ px: 0.5, pt: 1, fontSize: '0.8125rem', color: 'text.secondary' }}>{t('assistente.historico.erro')}</Typography>
        )}
        {!isLoading && !isError && filtradas.length === 0 && (
          <Box sx={{ px: 0.5, pt: 3, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.primary' }}>
              {t(busca.trim()
                ? 'assistente.historico.semResultado'
                : arquivadas ? 'assistente.historico.vazioArquivadas' : 'assistente.historico.vazio')}
            </Typography>
            {!busca.trim() && !arquivadas && (
              <Typography sx={{ mt: 0.5, fontSize: '0.8125rem', color: 'text.secondary' }}>{t('assistente.historico.vazioDica')}</Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          {filtradas.map((conversa) => (
            <LinhaDaConversa
              key={conversa.uuid}
              conversa={conversa}
              ativa={conversa.uuid === conversaAtual}
              onAbrir={() => abrirConversa(conversa.uuid)}
              onMenu={(el) => setMenu({ el, uuid: conversa.uuid })}
            />
          ))}
        </Box>
      </Box>

      <Menu
        anchorEl={menu?.el ?? null}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {arquivadas ? (
          <MenuItem dense onClick={aoDesarquivar}>
            <ListItemIcon><ArchiveRestore size={15} /></ListItemIcon>
            {t('assistente.conversa.desarquivar')}
          </MenuItem>
        ) : (
          <MenuItem dense onClick={aoArquivar}>
            <ListItemIcon><Archive size={15} /></ListItemIcon>
            {t('assistente.conversa.arquivar')}
          </MenuItem>
        )}
        <MenuItem
          dense
          onClick={() => {
            const uuid = menu?.uuid ?? null
            setMenu(null)
            setApagando(uuid)
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><Trash2 size={15} /></ListItemIcon>
          {t('assistente.conversa.apagar')}
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={Boolean(apagando)}
        title={t('assistente.conversa.apagarTitulo')}
        message={t('assistente.conversa.apagarTexto')}
        confirmLabel={t('assistente.conversa.apagar')}
        danger
        loading={apagar.isPending}
        onConfirm={confirmarApagar}
        onClose={() => setApagando(null)}
      />
    </Box>
  )
}
